import {useRecoilValue} from "recoil";
import userState from "../utils/user-state";
import {useContext, useEffect, useState} from "react";
import LayoutSplit from "../components/layout-split";
import Heading from "../components/heading";
import Text from "../components/text";
import FirebaseContext from "../utils/firebase-context";
import Request from "../models/request";
import List from "../components/list";
import ListItem from "../components/list-item";
import Modal from "../components/modal";
import styled from "styled-components";
import Checkbox from "../components/checkbox";
import Permission from "../models/permission";
import User from "../models/user";

const Name = styled.div`
	font-weight: 800;
	font-size: 1.3rem;
`;

const Email = styled.div`
	font-size: 0.9rem;
`;

const AcceptButton = styled.button`
	border: none;
	padding: .5rem 0;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #11ad67;
	font-weight: 800;
	background: #d8ffe9;
	border-radius: 12px;
	transition: .1s;

	&:hover {
		background: #c0ffd8;
	}
`;
const DeclineButton = styled.button`
	border: none;
	padding: .5rem 0;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #ad1111;
	font-weight: 800;
	background: #ffd8d8;
	border-radius: 12px;
	transition: .1s;

	&:hover {
		background: #ffc0c0;
	}
`;

const ModalHeading = styled.div`
	font-weight: 800;
	margin-bottom: .5rem;
`;

const initialOptions = {
		blogs: false,
		pages: false,
		components: false
};

function getPermissions(object: {[key: string]: boolean}) {
		let permissions = "";
		for (let o of Object.entries(object)) {
				permissions += o[1] ? (permissions === "" ? o[0] : "," + o[0]) : "";
		}

		return permissions;
}

export default function AdminScreen() {
		const user = useRecoilValue(userState);
		const firebase = useContext(FirebaseContext);
		const [loading, setLoading] = useState(true);
		const [accepting, setAccepting] = useState(false);
		const [allowed, setAllowed] = useState(false);

		const [showModal, setShowModal] = useState(false);

		const [requests, setRequests] = useState<Request[]>([]);
		const [request, setRequest] = useState<Request>();

		const [users, setUsers] = useState<User[]>([]);

		useEffect(() => {
				if (!user) return;
				if (user.admin) {
						setAllowed(true);
				}
				setLoading(false);
		}, [user]);

		useEffect(() => {
				return firebase.getRequests().onSnapshot(snapshot => {
						const requests: Request[] = [];
						snapshot.forEach(doc => {
								requests.push(doc.data() as Request);
						})

						setRequests(requests);
				});
		}, [firebase]);

		useEffect(() => {
				return firebase.getUsers().onSnapshot(snapshot => {
						const us: User[] = [];
						snapshot.forEach(doc => {
								us.push(doc.data() as User);
						})
						setUsers(us);
				});
		}, [firebase]);

		const loadingComponent = <div>Loading...</div>

		const openModal = (request: Request) => () => {
				setRequest(request);
				setShowModal(true);
		};

		const noPermission = <div>
				Du darfst hier nicht sein! 🤫
		</div>

		const [options, setOptions] = useState(initialOptions);

		const toggleOption = (option: Permission) => () => {
				setOptions(o => ({...o, [option]: !o[option]}));
		}

		const close = () => {
				setShowModal(false);
		};

		const accept = async () => {
				if (!request) return;
				setAccepting(true);
				await firebase.acceptRequest(request, getPermissions(options));
				setAccepting(false);
				close();
		};

		const decline = async () => {
				if (!request) return;
				setAccepting(true);
				await firebase.deleteRequest(request);
				setAccepting(false);
				close();
		};

		return loading ? loadingComponent : !allowed ? noPermission : <>
				{requests.length > 0 && <LayoutSplit>
						<div>
								<Heading>
										Anfragen
								</Heading>
								<Text>
										Hier findest du alle Account-Anfragen
								</Text>
						</div>
						<div>
								<List>
										{requests.map(r => <ListItem key={r.email} onClick={openModal(r)}>
												<div>
														<div style={{
																fontWeight: 700,
																fontSize: "1.2rem",
														}}>
																{r.firstName + " " + r.lastName}
														</div>
														<div style={{
																fontSize: "0.8rem",
																fontWeight: 300,
														}}>
																{r.email}
														</div>
												</div>
										</ListItem>)}
								</List>
						</div>
				</LayoutSplit>}
				{users.length > 1 && <LayoutSplit>
						<div>
								<Heading>
										Account-Übersicht
								</Heading>
								<Text>
										Hier kannst du alle Accounts sehen, bearbeiten und Berechtigungen modifizieren
								</Text>
						</div>
						<div>
								<List>
										{users.filter(u => u.email !== user?.email).map(u => <ListItem>
												<div>
														<Name>
																{u.firstName + " " + u.lastName}
														</Name>
														<Email>
																{u.email}
														</Email>
												</div>
										</ListItem>)}
								</List>
						</div>
				</LayoutSplit>}
				{users.length <= 1 && <LayoutSplit>
						<div>
								<Heading>
										Admin
								</Heading>
								<Text>
										Das hier ist das Admin-Panel.
										Hier kannst du Nutzer verwalten und die App-Aktivität checken.
										Die App hat momentan noch keine Nutzer.
								</Text>
						</div>
				</LayoutSplit>}
				{showModal && !!request && <Modal close={() => setShowModal(false)} title={"Anfrage bearbeiten"}>
						<div style={{
								marginBottom: "1rem",
						}}>
								<Name>
										{request.firstName + " " + request.lastName}
								</Name>
								<Email>
										{request.email}
								</Email>
						</div>
						<div style={{
								marginBottom: "1rem",
						}}>
								<ModalHeading>Berechtigungen</ModalHeading>
								<div style={{
										display: "grid",
										gap: ".25rem",
								}}>
										{Object.entries(options).map(o => {
												return <Checkbox key={o[0]} text={o[0]} active={o[1]} onClick={toggleOption(o[0] as Permission)} />
										})}
								</div>
						</div>
						<AcceptButton onClick={accept} disabled={accepting}>Anfrage annehmen</AcceptButton>
						<DeclineButton onClick={decline} disabled={accepting}>Anfrage ablehnen</DeclineButton>
				</Modal>}
		</>
}